import { type MessagesKeys, useTranslate } from '@repo/shared/hooks'
import { globalConfig } from '@repo/shared/utils'
import {
  AspectRatio,
  Badge,
  Button,
  Col,
  DotFallingLoader,
  Icon,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalTrigger,
  Row,
  Typography,
} from '@repo/ui/components'

import Image from 'next/image'
import * as React from 'react'
import { OrderBoxAssetSelectItem } from './order-box.asset-select.item'
import { OrderBoxAssetSelectSellItem } from './order-box.asset-select.sell-item'
import type { OrderBoxAssetSelectProps } from './order-box.asset-select.types'
import { type FlatAsset } from '.'
import { useDebounce } from '@repo/ui/hooks'
import { cn } from '@repo/ui/lib'
import { Cookie } from '@repo/tools'

export const getLogoUrl = (url?: string) =>
  url ? `${globalConfig.baseImageUrl}${url}` : '/images/placeholder.webp'

export const OrderBoxAssetSelect: React.FC<OrderBoxAssetSelectProps> = ({
  selectedAsset,
  onChange,
  error,
  tokenClassName,
  iconClassName,
  containerClassName,
  projects,
  tab = 'buy',
  isTransfer = false,
  listProjectsData,
  tokenType = 'all',
  onSearchChange,
  displayBadge = true,
}) => {
  const { t } = useTranslate()
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const [hasAutoSelected, setHasAutoSelected] = React.useState(false)
  const [urlProjectId, setUrlProjectId] = React.useState<string | null>(null)
  const debouncedSearch = useDebounce(search, 400) || ''
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)
  const isLogin = Cookie.get('access_token') ? true : false

  // Handle search change
  React.useEffect(() => {
    onSearchChange?.(debouncedSearch)
  }, [debouncedSearch, onSearchChange])

  // Get projectId from URL (SSR-safe)
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)
      const projectId = urlParams.get('projectId')
      setUrlProjectId(projectId)
    }
  }, [])

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)
      const projectId = urlParams.get('projectId')
      if (tab === 'sell' && !projectId) {
        setOpen(true)
      } else {
        setOpen(false)
      }
    }
  }, [tab])

  // Use passed data instead of hook
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    listProjectsData || {
      data: [] as FlatAsset[],
      fetchNextPage: () => {},
      hasNextPage: false,
      isFetchingNextPage: false,
      isLoading: false,
    }

  // Use projects data when in sell mode, otherwise use API data
  const shouldUseProjects = tab === 'sell' && projects && projects.length > 0

  const handleLoadMore = React.useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const assets = React.useMemo(() => {
    // Use user assets data when in sell mode
    if (shouldUseProjects) {
      return projects
        .filter((project: any) => {
          if (!search) return true
          return (
            project.displayName?.toLowerCase().includes(search.toLowerCase()) ||
            project.token?.toLowerCase().includes(search.toLowerCase()) ||
            project.currency?.toLowerCase().includes(search.toLowerCase())
          )
        })
        ?.filter((project: any) =>
          tokenType === 'all' ? true : project.kind === tokenType,
        )
        .map((project: any) => ({
          id: project.id?.toString() || project.currency || '',
          name: project.name || project.displayName || project.currency || '',
          image: getLogoUrl(project?.logoURL) || '',
          price: project.price?.priceRange24h?.last || 0,
          pricePerMeter: project.price?.priceRange24h?.last || 0,
          about: project.description || '',
          province:
            project.kind === 'property'
              ? project.location?.province?.name || ''
              : undefined,
          county:
            project.kind === 'property'
              ? project.location?.county?.name || ''
              : undefined,
          city:
            project.kind === 'property'
              ? project.location?.city?.name || ''
              : undefined,
          district:
            project.kind === 'property'
              ? project.location?.district?.name || ''
              : undefined,
          type:
            project.kind === 'property'
              ? (project.type as 'initial' | 'final') || 'initial'
              : undefined,
          state: project.kind === 'property' ? project.state || '' : undefined,
          tradeStatus: project.tradeStatus || 'active',
          percentChange: project.price?.priceRange24h?.deltaPercent,
          description: project.description || '',
          usage: t(`filters_usage_${project.type}` as MessagesKeys) || '',
          galleryImages: (project.photos
            ?.map((photo: any) => getLogoUrl(photo.smallURL))
            .filter((url: any): url is string => Boolean(url)) ||
            []) as string[],
          specs: [],
          totalArea: { m2: 0, cm2: 0 },
          offeredArea: { m2: 0, cm2: 0 },
          purchasableArea: { m2: 0, cm2: 0 },
          priceChartData: [],
          logoURL: project.logoURL,
          location: { lat: 0, lng: 0 },
          token: project.token || project.currency || '',
          kind: project.kind || '',
          // Add user portfolio data for sell mode
          availableBalance: project.availableBalance || 0,
          balance: project.balance || 0,
          currency: project.currency || '',
          accountType: project.type || 'project',
          estimatedAvailableBalance: project.estimatedAvailableBalance || 0,
          scale: project.scale || 1,
          minOrderSize: project.market?.minOrderSize || 0,
        }))
    }
    // Use API data for buy mode
    return (
      (data as any)?.pages
        ?.flatMap((page: any) => page.data?.projects || [])
        ?.filter((project: any) =>
          tokenType === 'all' ? true : project.kind === tokenType,
        )
        ?.map((project: any) => ({
          id: project.id?.toString() || '',
          name: project.name || project.displayName || '',
          image: getLogoUrl(project?.logoURL) || '',
          price: project.price?.priceRange24h?.last || 0,
          pricePerMeter: project.price?.priceRange24h?.last || 0,
          about: project.description || '',
          province:
            project.kind === 'property'
              ? project.location?.province?.name || ''
              : undefined,
          county:
            project.kind === 'property'
              ? project.location?.county?.name || ''
              : undefined,
          city:
            project.kind === 'property'
              ? project.location?.city?.name || ''
              : undefined,
          district:
            project.kind === 'property'
              ? project.location?.district?.name || ''
              : undefined,
          type:
            project.kind === 'property'
              ? (project.type as 'initial' | 'final') || 'initial'
              : undefined,
          state: project.kind === 'property' ? project.state || '' : undefined,
          tradeStatus: project.tradeStatus || 'active',
          kind: project.kind || '',
          percentChange: project.price?.priceRange24h?.deltaPercent,
          description: project.description || '',
          usage: t(`filters_usage_${project.type}` as MessagesKeys) || '',
          galleryImages: (project.photos
            ?.map((photo: any) => getLogoUrl(photo.smallURL))
            .filter((url: any): url is string => Boolean(url)) ||
            []) as string[],
          specs: [],
          totalArea: { m2: 0, cm2: 0 },
          offeredArea: { m2: 0, cm2: 0 },
          purchasableArea: { m2: 0, cm2: 0 },
          priceChartData: [],
          logoURL: project.logoURL,
          location: { lat: 0, lng: 0 },
          token: project.token || '',
          scale: project.scale || 1,
          minOrderSize: project.market?.minOrderSize || 0,
        })) || []
    )
  }, [data, t, shouldUseProjects, projects, search])

  const handleScroll = React.useCallback(() => {
    // Don't handle scroll for projects data (sell mode)
    if (shouldUseProjects) {
      return
    }

    if (
      !scrollContainerRef.current ||
      !hasNextPage ||
      isFetchingNextPage ||
      !handleLoadMore
    ) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50 // Reduced threshold to 50px

    if (isNearBottom) {
      handleLoadMore()
    }
  }, [hasNextPage, isFetchingNextPage, handleLoadMore, shouldUseProjects])

  React.useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (scrollContainer && open) {
      // Add a small delay to ensure the modal is fully rendered
      const timer = setTimeout(() => {
        scrollContainer.addEventListener('scroll', handleScroll)
      }, 100)

      return () => {
        clearTimeout(timer)
        scrollContainer.removeEventListener('scroll', handleScroll)
      }
    }
  }, [handleScroll, open])

  const handleAssetSearch = (value: string) => {
    setSearch(value)
  }

  const handleOpenChange = (open: boolean) => {
    setOpen(open)
    // Clear search when opening
    if (open) {
      setSearch('')
    }
    // Reset auto-selection flag when modal is closed
    if (!open) {
      setHasAutoSelected(false)
    }
  }

  // Auto-select first asset if none is selected (only for buy mode)
  React.useEffect(() => {
    if (
      !selectedAsset &&
      assets.length > 0 &&
      !isLoading &&
      !debouncedSearch &&
      !open &&
      !hasAutoSelected &&
      tab === 'buy'
    ) {
      onChange(assets[0] as unknown as FlatAsset)
      setHasAutoSelected(true)
    }
  }, [
    assets,
    selectedAsset,
    onChange,
    isLoading,
    debouncedSearch,
    open,
    hasAutoSelected,
    tab,
  ])

  // Reset auto-selection flag when selectedAsset changes externally
  React.useEffect(() => {
    if (selectedAsset) {
      setHasAutoSelected(true)
    } else {
      setHasAutoSelected(false)
    }
  }, [selectedAsset])

  // Clear selection when switching to sell mode
  React.useEffect(() => {
    if (tab === 'sell') {
      if (urlProjectId) {
        onChange(
          assets.find(
            (asset: FlatAsset) => asset.token === urlProjectId,
          ) as unknown as FlatAsset,
        )
      } else {
        onChange(null as any)
      }
    } else {
      if (tokenType === 'all' && selectedAsset?.kind === 'crypto') {
        onChange(assets[0] as unknown as FlatAsset)
      }
      if (urlProjectId === 'MALEK') {
        onChange(null as any)
      }
    }
  }, [tab, urlProjectId]) // Only depend on tab, not selectedAsset

  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      <ModalTrigger asChild>
        <Button
          type='button'
          variant='transparent'
          width='full'
          className={cn(
            'justify-between rounded-md p-md gap-md text-neutral-main border',
            error ? 'border-negative-surface-100' : 'border-neutral-surface',
            containerClassName,
          )}
        >
          <Row className='gap-xs sm:ap-md w-full min-h-5xl'>
            {selectedAsset && (
              <div className={cn('w-3xl h-3xl', iconClassName)}>
                <AspectRatio className=' aspect-1/1 rounded-2xs'>
                  <Image
                    src={
                      selectedAsset.name === 'MALEK'
                        ? '/images/club-gem.svg'
                        : getLogoUrl(selectedAsset?.logoURL) ||
                          '/images/placeholder.webp'
                    }
                    alt='Photo of property'
                    fill
                    sizes='48px'
                    priority
                    className='rounded-sm object-cover'
                  />
                </AspectRatio>
              </div>
            )}
            <Col className='flex-1 items-start gap-xs'>
              <Row className='gap-2xs'>
                <Typography
                  size='sm'
                  truncate
                  className={cn(
                    'max-w-[150px] sm:!max-w-[180px]',
                    tokenClassName,
                  )}
                >
                  {selectedAsset?.name === 'MALEK'
                    ? t('common_square_token_title')
                    : selectedAsset?.name || t('common_select_asset')}
                </Typography>
              </Row>

              <Typography
                size='xs'
                color='text-neutral-surface-60'
                className='font-[AzarMehr]'
              >
                {selectedAsset?.token === 'MALEK'
                  ? 'SQ²'
                  : selectedAsset?.token || ''}
              </Typography>
            </Col>
            {displayBadge && selectedAsset && selectedAsset?.state && (
              <Badge size='sm' variant='liquid' color={'base'}>
                {t(`common_${selectedAsset?.state}` as MessagesKeys)}
              </Badge>
            )}
            <Icon name='TriangleDown' size='sm' />
          </Row>
        </Button>
      </ModalTrigger>
      <ModalContent className='sm:max-w-[480px] p-0 py-md'>
        <ModalHeader>
          <ModalTitle>
            {tab === 'sell'
              ? isTransfer
                ? t('transfer_token')
                : t('sell_token')
              : t('common_asset_select')}
          </ModalTitle>
        </ModalHeader>
        <div className='px-md'>
          <Input
            value={search}
            onChange={(e) => handleAssetSearch(e.target.value)}
            placeholder={t('common_search')}
            icon={
              <Icon
                name='SearchLine'
                size='sm'
                color='fill-neutral-surface-40'
              />
            }
            endIcon={
              search && (
                <Icon
                  name='XmarkCircle'
                  size='sm'
                  color='fill-neutral-surface-40'
                  onClick={() => setSearch('')}
                />
              )
            }
            inputSize='md'
            autoComplete='off'
          />
        </div>
        <div
          ref={scrollContainerRef}
          className='max-h-[60vh] overflow-y-auto mt-md relative'
          onScroll={handleScroll}
        >
          <Col className='gap-md'>
            {isLogin && tab === 'sell' && (
              <div className='px-md'>
                <Typography
                  size='sm'
                  weight='bold'
                  color='text-neutral-surface-80'
                >
                  {t('my_assets')}
                </Typography>
              </div>
            )}
            {assets.length <= 0 &&
              !search &&
              !isLoading &&
              !isFetchingNextPage && (
                <Col className='justify-center items-center gap-md py-md min-h-[240px]'>
                  <Image
                    src='/images/search.webp'
                    alt='empty-state'
                    width={64}
                    height={64}
                  />
                  <Typography color='text-base-surface-100'>
                    {t(
                      tab === 'sell'
                        ? isTransfer
                          ? 'common_no_transfer_asset'
                          : 'common_no_sell_asset'
                        : 'common_no_buy_asset',
                    )}
                  </Typography>
                </Col>
              )}

            {assets.length <= 0 &&
              search &&
              !isLoading &&
              !isFetchingNextPage && (
                <Col className='justify-center items-center gap-md py-md min-h-[240px]'>
                  <Image
                    src='/images/search.webp'
                    alt='empty-state'
                    width={64}
                    height={64}
                  />
                  <Typography color='text-base-surface-100'>
                    {t('common_no_result')}
                  </Typography>
                </Col>
              )}

            {assets.map((asset: FlatAsset) => (
              <React.Fragment key={asset.id}>
                {tab === 'sell' ? (
                  <OrderBoxAssetSelectSellItem
                    asset={asset as unknown as FlatAsset}
                    onClick={() => {
                      onChange(asset as unknown as FlatAsset)
                      setOpen(false)
                      setHasAutoSelected(true) // Mark as manually selected
                      // Clear search when asset is selected
                      setSearch('')
                    }}
                  />
                ) : (
                  <OrderBoxAssetSelectItem
                    asset={asset as unknown as FlatAsset}
                    onClick={() => {
                      onChange(asset as unknown as FlatAsset)
                      setOpen(false)
                      setHasAutoSelected(true) // Mark as manually selected
                      // Clear search when asset is selected
                      setSearch('')
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </Col>
        </div>
        {(isLoading || isFetchingNextPage) && (
          <div className='absolute rounded-b-md bottom-0 left-0 right-0 flex justify-center items-center py-md bg-gradient-to-t from-neutral-surface via-neutral-surface/80 to-transparent pointer-events-none z-10'>
            <DotFallingLoader size='md' color='neutral' />
          </div>
        )}
      </ModalContent>
    </Modal>
  )
}
